import { Entity } from '@api/api/entity-schema';
import { ExploreSingleInsightWithMeta } from '@api/api/insights';
import { isToday } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { getConditionIcons } from '../../insight-icons/utils';
import { InsightIconsRoot } from '../../insight-icons-root/InsightIconsRoot';
import { InsightsTooltip } from '../../insights-tooltip/InsightsTooltip';
import styles from './ChartInsightIcons.module.scss';

const BOTTOM_PADDING = 40;

const SINGLE_ICON_WIDTH = 12;
const SINGLE_ICON_HEIGHT = 14;

const VERTICAL_COMBINED_ICON_WIDTH = 12;
const VERTICAL_COMBINED_ICON_HEIGHT = 24;

const SINGLE_ICONS_SIZE = {
  width: `${SINGLE_ICON_WIDTH}px`,
  height: `${SINGLE_ICON_HEIGHT}px`,
};
const COMBINE_ICONS_SIZE = {
  width: `${VERTICAL_COMBINED_ICON_WIDTH}px`,
  height: `${VERTICAL_COMBINED_ICON_HEIGHT}px`,
};

type ExploreInsightWithRight = ExploreSingleInsightWithMeta & {
  right?: number;
  yOffsetCount: number;
};
export interface ChartInsightIconsProps {
  articleInsights: ExploreInsightWithRight[];
  entity: Entity;
  handleSetActiveInsightId: (id: string) => void;
}

// Insight drawer
export const ChartInsightIcons = ({
  articleInsights,
  entity,
  handleSetActiveInsightId,
}: ChartInsightIconsProps) => {
  const { t } = useTranslation(['insights']);
  return (
    <>
      {articleInsights.map(insight => {
        const { right, yOffsetCount } = insight;

        if (right === undefined) {
          return null;
        }

        const direction = insight.direction;
        const isStarred = isToday(new Date(insight.created_at));

        const conditions = getConditionIcons(insight);

        const isCombined = conditions.length > 1;
        const ICON_WIDTH = isCombined
          ? VERTICAL_COMBINED_ICON_WIDTH
          : SINGLE_ICON_WIDTH;
        const CENTER_PADDING = ICON_WIDTH / 2;
        const ICON_HEIGHT = isCombined
          ? VERTICAL_COMBINED_ICON_HEIGHT
          : SINGLE_ICON_HEIGHT;

        //TODO: PLATFORM-277 - TECH DEBT - I18N
        const episodeLabel = t('insightCard.episode', {
          count: insight.episode_count,
        });

        return (
          <InsightsTooltip
            key={insight.id}
            conditions={conditions}
            direction={direction}
            customSize={{ width: '27px', height: '32px' }}
            isStarred={isToday(new Date(insight.created_at))}
            isMatched
            horizon={insight.best_horizon}
            episodeLabel={episodeLabel}
            median={insight.median}
            stars={insight.stars}
            name={insight.card.name}
            entityTag={entity.tag}
          >
            <div
              className={styles.iconWrapper}
              style={
                {
                  bottom: `${BOTTOM_PADDING + yOffsetCount * ICON_HEIGHT}px`,
                  '--right': `${right - CENTER_PADDING}px`,
                } as React.CSSProperties
              }
              data-testid="chart-insight-icons"
              onClick={() => handleSetActiveInsightId(insight.id)}
            >
              <InsightIconsRoot
                conditions={conditions}
                direction={direction}
                customSize={isCombined ? COMBINE_ICONS_SIZE : SINGLE_ICONS_SIZE}
                isStarred={isStarred}
                isCombined={isCombined}
                layout="vertical"
                isSparklesCondensed
              />
            </div>
          </InsightsTooltip>
        );
      })}
    </>
  );
};
